import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { I18nextProvider } from "react-i18next"; // Added this
import i18n from "../i18n"; // <-- Explicitly import the default export
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";
import { Toaster } from "sonner";

import appCss from "../styles.css?url";
import heroWorkshop from "../assets/hero-workshop.jpg";

import { renderErrorPage } from "../lib/error-page"; // Imported your custom error page layout

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error("Caught error in boundary:", error);
  const router = useRouter();

  // If we are executing on the Node server during SSR and a critical error happens,
  // completely swap the layout out with your clean custom standalone HTML template.
  if (typeof window === "undefined") {
    return <div dangerouslySetInnerHTML={{ __html: renderErrorPage() }} />;
  }

  // Interactive client-side fallback UI
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "Sony Tyres Kolhapur — Authorised Multibrand Tyre Dealer" },
      {
        name: "description",
        content:
          "Sony Tyres, Kolhapur — 40+ years of authorised multibrand tyre service. JK, CEAT, Goodyear, Kelly, TVS, Ralco. Sales, fitting, balancing & nitrogen.",
      },
      { name: "author", content: "Sony Tyres" },
      { property: "og:title", content: "Sony Tyres Kolhapur" },
      {
        property: "og:description",
        content:
          "Authorised multibrand tyre dealer in Kolhapur, serving since 1986. Expert sales, fitting, balancing & nitrogen refilling.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://sonytyres.in/" },
      { property: "og:image", content: "https://mkt-dev.sonytyres.in/assets/logo-DG82WAHD.png" },
      { property: "og:image:alt", content: "Sony Tyres logo" },
    ],
    links: [
      { rel: "canonical", href: "https://sonytyres.in/" },
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "alternate", hreflang: "en", href: "https://sonytyres.in/" },
      { rel: "alternate", hreflang: "hi", href: "https://sonytyres.in/?lang=hi" },
      { rel: "alternate", hreflang: "mr", href: "https://sonytyres.in/?lang=mr" },
      { rel: "alternate", hreflang: "x-default", href: "https://sonytyres.in/" },
      { rel: "preload", href: heroWorkshop, as: "image", fetchPriority: "high" },
      { rel: "stylesheet", href: appCss },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,300..900;1,9..144,300..900&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;700&family=Noto+Sans+Devanagari:wght@400;500;600;700&family=Noto+Serif+Devanagari:wght@400;500;700&display=swap",
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Sony Tyres",
    "image": "https://mkt-dev.sonytyres.in/assets/logo-DG82WAHD.png",
    "description": "Authorised multibrand tyre dealer in Kolhapur serving since 1986. Expert sales, fitting, balancing and nitrogen refilling.",
    "url": "https://sonytyres.in",
    "telephone": "+91-231-2653947",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "333 E, Station Road, Prabhakar Plaza",
      "addressLocality": "Kolhapur",
      "addressRegion": "Maharashtra",
      "postalCode": "416001",
      "addressCountry": "IN"
    },
    "openingHoursSpecification": [
      {
        "@type": "OpeningHoursSpecification",
        "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
        "opens": "09:30",
        "closes": "19:30"
      }
    ],
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "17.6639",
      "longitude": "73.8315"
    }
  };

  return (
    <html lang="en">
      <head>
        <HeadContent />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <I18nextProvider i18n={i18n}>
        <Outlet />
        <Toaster richColors position="top-center" />
      </I18nextProvider>
    </QueryClientProvider>
  );
}